import { css } from '@emotion/react';

const wrapper = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 56px 0;
  flex: 1;

  @media (max-width: 1169px) {
    padding: 48px 16px;
  }
`;

const headerArea = css`
  width: 1140px;
  max-width: 100%;
`;

const card = css`
  width: 1140px;
  max-width: 100%;
  background: #fff;
  border-radius: 10px;
  padding: 32px 40px;
  box-shadow:
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05);
`;

const cardTitle = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #241a17;
  margin: 0 0 24px;
`;

const statsRow = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
`;

const stat = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const statValue = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 44px;
  color: #241a17;
`;

const statLabel = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: rgba(57, 48, 45, 0.52);
  margin-top: 8px;
`;

const chartHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const chartFilters = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const select = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid rgba(60, 50, 47, 0.12);
  border-radius: 6px;
  background: #fff;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #241a17;
`;

const toggleRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const toggle = css`
  position: relative;
  width: 32px;
  height: 18px;
  background: #251b18;
  border-radius: 100px;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    right: 2px;
    width: 14px;
    height: 14px;
    background: #fff;
    border-radius: 50%;
  }
`;

const chart = css`
  position: relative;
  height: 280px;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding: 24px 0 24px 32px;
  border-bottom: 1px solid rgba(60, 50, 47, 0.12);
`;

const yAxis = css`
  position: absolute;
  top: 24px;
  bottom: 24px;
  left: 0;
  width: 24px;
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: rgba(57, 48, 45, 0.52);
`;

const bar = css`
  flex: 1;
  background: #92ada6;
  border-radius: 2px 2px 0 0;
  min-width: 8px;
`;

const tabSecondary = css`
  display: flex;
  gap: 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid rgba(60, 50, 47, 0.12);
`;

const tabSecondaryItem = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: rgba(61, 46, 41, 0.52);
  cursor: pointer;
  padding: 12px 0;
  background: none;
  border: none;
`;

const tabSecondaryActive = css`
  ${tabSecondaryItem};
  color: #241a17;
  border-bottom: 2px solid #241a17;
`;

// Synthetic 24-week data; one bar visibly taller to mimic the screenshot
const weeks = Array.from({ length: 24 }, (_, i) => ({
  id: `w${i}`,
  value: i === 18 ? 3 : 0,
}));
const maxBar = 3;

export default function AnalyticsView() {
  return (
    <div css={wrapper}>
      <div css={headerArea}>
        <div css={tabSecondary}>
          <button css={tabSecondaryActive} type="button">
            Subscribers
          </button>
          <button css={tabSecondaryItem} type="button">
            Emails
          </button>
          <button css={tabSecondaryItem} type="button">
            Forms
          </button>
          <button css={tabSecondaryItem} type="button">
            Workflows
          </button>
          <button css={tabSecondaryItem} type="button">
            Checkouts
          </button>
        </div>
      </div>

      <div css={card}>
        <h2 css={cardTitle}>Subscribers at a glance</h2>
        <div css={statsRow}>
          <div css={stat}>
            <div css={statValue}>5,021</div>
            <div css={statLabel}>Total active subscribers</div>
          </div>
          <div css={stat}>
            <div css={statValue}>0</div>
            <div css={statLabel}>New last 7 days</div>
          </div>
          <div css={stat}>
            <div css={statValue}>0</div>
            <div css={statLabel}>New last 30 days</div>
          </div>
        </div>
      </div>

      <div css={card}>
        <div css={chartHeader}>
          <h2 css={cardTitle} style={{ marginBottom: 0 }}>
            Subscriber growth
          </h2>
          <div css={toggleRow}>
            <span css={toggle} />
            <span
              css={css`
                font-family: 'FlodeskSans', sans-serif;
                font-weight: 400;
                font-size: 14px;
                color: #241a17;
              `}
            >
              Show source
            </span>
          </div>
        </div>
        <div css={chartFilters}>
          <span css={select}>
            New subscribers
            <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
              <path
                d="M1 7L5 11L9 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span css={select}>
            In the last 24 weeks
            <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
              <path
                d="M1 7L5 11L9 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
        <div css={chart}>
          <div css={yAxis}>
            <span>0</span>
            <span>1</span>
            <span>2</span>
            <span>3</span>
          </div>
          {weeks.map(w => (
            <div
              key={w.id}
              css={bar}
              style={{ height: w.value === 0 ? '1px' : `${(w.value / maxBar) * 90}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
